import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, SectionEyebrow, Pill, Icons } from '@dhuttu/ui-components';
import { useToast } from '@dhuttu/ui';
import { useAuth } from '../auth/useAuth';
import { makeApi } from '../api/client';
import { makeUsersApi, type UserView, type CreateUserRequest, UsersApiError } from '../api/users';
import { CashierFormDialog } from '../components/CashierFormDialog';
import { CashierEditDialog } from '../components/CashierEditDialog';
import { OfflinePasswordDrawer } from '../components/OfflinePasswordDrawer';
import { CashierRowMenu } from '../components/CashierRowMenu';
import type { CashierFormInput, CashierEditInput } from './cashiers.schema';

/**
 * Slice 11 — Cashiers page. List of merchant-side cashier identities with
 * inline status, add / edit / activate / deactivate actions, and an
 * offline-password rotation drawer.
 *
 * The list comes from /v1/users (hand-wired in `api/users.ts` until the
 * generated `@dhuttu/contracts` client picks the operations up). Stores for
 * the picker reuse the existing org listStores endpoint.
 */
export default function CashiersRoute() {
  const { getAccessToken } = useAuth();
  const usersApi = makeUsersApi(getAccessToken);
  const api = makeApi(getAccessToken);
  const qc = useQueryClient();
  const { toast } = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserView | null>(null);
  const [pwdTarget, setPwdTarget] = useState<UserView | null>(null);
  const [createError, setCreateError] = useState<UsersApiError | null>(null);

  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers(),
  });
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: async (): Promise<{ items: Array<{ id: string; name: string }> }> => {
      const res = await api.org.listStores();
      // The generated client types this as a union; runtime shape is { items }.
      return (res.data as unknown as { items: Array<{ id: string; name: string }> }) ?? { items: [] };
    },
  });
  const storeOptions = (storesQ.data?.items ?? []).map((s) => ({ id: s.id, name: s.name }));
  const storeNameById = new Map(storeOptions.map((s) => [s.id, s.name] as const));

  const create = useMutation({
    mutationFn: (d: CashierFormInput): Promise<UserView> => usersApi.createUser(d as CreateUserRequest),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setAddOpen(false);
      setCreateError(null);
      toast({ kind: 'success', title: 'Cashier created' });
    },
    onError: (err) => {
      if (err instanceof UsersApiError) setCreateError(err);
      else toast({ kind: 'error', title: 'Failed to create cashier' });
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof usersApi.patchUser>[1] }) =>
      usersApi.patchUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: () => toast({ kind: 'error', title: 'Update failed' }),
  });

  const setPwd = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      usersApi.setOfflinePassword(id, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setPwdTarget(null);
      toast({ kind: 'success', title: 'Offline password updated' });
    },
    onError: () => toast({ kind: 'error', title: 'Failed to set offline password' }),
  });

  const clearPwd = useMutation({
    mutationFn: (id: number) => usersApi.clearOfflinePassword(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setPwdTarget(null);
      toast({ kind: 'success', title: 'Offline password cleared' });
    },
    onError: () => toast({ kind: 'error', title: 'Failed to clear offline password' }),
  });

  const items = usersQ.data?.items ?? [];

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SectionEyebrow>Cashiers</SectionEyebrow>
          <span className="inline-block min-w-[22px] rounded-full bg-[color:var(--v2-sunk)] px-1.5 text-center text-[10px] font-bold leading-[16px] text-[color:var(--v2-ink-soft)] tabular-nums">
            {items.length}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { setCreateError(null); setAddOpen(true); }}
          className="inline-flex items-center gap-1 rounded-md border border-[color:var(--v2-stroke)] px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--v2-ink-soft)] transition hover:bg-[color:var(--v2-sunk)] hover:text-[color:var(--v2-ink)]"
        >
          <Icons.PlusIcon className="h-3 w-3" /> Add cashier
        </button>
      </header>

      <Card padding="none">
        {usersQ.isLoading && (
          <p className="px-3 py-8 text-center text-xs text-[color:var(--v2-mute)]">Loading…</p>
        )}
        {!usersQ.isLoading && items.length === 0 && (
          <div className="px-3 py-10 text-center">
            <p className="text-sm font-semibold text-[color:var(--v2-ink)]">No cashiers yet</p>
            <p className="mt-1 text-[11px] text-[color:var(--v2-mute)]">
              Add a cashier so they can sign into the POS app.
            </p>
          </div>
        )}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[color:var(--v2-mute)]">
              <tr className="border-b border-[color:var(--v2-stroke)]">
                <th className="px-3 py-2 text-left font-semibold">Name</th>
                <th className="px-3 py-2 text-left font-semibold">Email</th>
                <th className="px-3 py-2 text-left font-semibold">Store</th>
                <th className="px-3 py-2 text-left font-semibold">Offline</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.userId} className="border-b border-[color:var(--v2-stroke)] last:border-b-0">
                  <td className="px-3 py-2 text-[color:var(--v2-ink)]">
                    {!u.active && <span className="mr-1 text-[color:var(--v2-mute)]">(inactive)</span>}
                    {u.displayName}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--v2-ink-soft)]">{u.email}</td>
                  <td className="px-3 py-2 text-[color:var(--v2-ink-soft)]">
                    {u.storeId ? (storeNameById.get(u.storeId) ?? u.storeId) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {u.offlinePasswordSet
                      ? <Pill tone="ok">Set</Pill>
                      : <Pill tone="neutral">Not set</Pill>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <CashierRowMenu
                      cashier={u}
                      onEdit={() => setEditTarget(u)}
                      onSetPassword={() => setPwdTarget(u)}
                      onToggleActive={() => patch.mutate({ id: u.userId, body: { active: !u.active } })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <CashierFormDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(d) => create.mutate(d)}
        stores={storeOptions}
        serverError={createError}
      />

      <CashierEditDialog
        open={!!editTarget}
        cashier={editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={(d: CashierEditInput) => {
          if (!editTarget) return;
          patch.mutate({ id: editTarget.userId, body: d }, {
            onSuccess: () => setEditTarget(null),
          });
        }}
        stores={storeOptions}
      />

      <OfflinePasswordDrawer
        open={!!pwdTarget}
        cashier={pwdTarget}
        onClose={() => setPwdTarget(null)}
        onSubmit={(password) => pwdTarget && setPwd.mutate({ id: pwdTarget.userId, password })}
        onClear={() => pwdTarget && clearPwd.mutate(pwdTarget.userId)}
        submitting={setPwd.isPending}
      />
    </section>
  );
}
